import React from "react";
import {abvNum, abvNumEx, funcOrValOr, getAgo, MS_DAY, msToDays, msToInputDate, secUnits, units, unitVals} from "./CommonJS";

export function query({site = "", api, params, onJson, alt, contentType, headers, onRsp}) {
    if (api?.[0] !== '/') api = '/'+api;
    const controller = new AbortController(), {signal} = controller;
    const hq = {
        api,
        isActive: true,
        created: new Date(),
        cancel: () => {
            if (!hq.isActive) return;
            hq.isActive = false;
            controller.abort(`Fetch aborted after ${getAgo(hq.created)}: ${api}`);
        }
    };

    function finish(value) {
        if (hq.isActive) {                  // if cancelled, don't issue callback.
            hq.isActive = false;
            onJson?.(value);
        }
    }

    (async () => {                  // put the query into async, to encapsulate the promise response
            fetch(site+ api, {
                method: "POST",
                signal,
                //credentials: "include",
                headers: {
                    ...(contentType === "" ? {} : {'content-type': contentType || 'application/json'}),
                    "Access-Control-Request-Method": "POST",
                    ...(typeof headers === "function"? await headers() : headers)
                },
                body: JSON.stringify(params),
                maxAge: 3600,
                //"mode": "no-cors",
            }).then(async rsp => {
                if (alt && rsp.status > 204 && process.env.REACT_APP_ENV?.toLowerCase().startsWith("dev"))
                    return finish(funcOrValOr(alt));
                if (rsp.status > 204) {
                    let text = await rsp.text(), json;
                    if (text?.[0] === "{") json = await rsp.json();
                    return finish({
                        error: rsp.status !== 416 ? `API "${api}" failed!\n\nError: ${rsp.status}${rsp.statusText? ` - ${rsp.statusText}` : json?.title || ""}` :
                            "Error: there is no role assigned to your account.",
                        errText: rsp.status === 416 ? "Please ask your GYTPOL administrator to assign a role to your account. This can be done by logging into the system, then pressing \"Permissions\" on the left menu." :
                            json?.errors? JSON.stringify(json.errors, null, 2).slice(2, -2) : text
                    });
                }
                if (onRsp?.(rsp)) return;
                rsp.json().then(finish).catch(err=>console.error(`Unable to parse ${api} response to JSON, error: ${err}`));
            }).catch(error => {
                console.log(error);
                finish({error})
            });
    })();

    return hq;
}

export function saveFile(name, {url, content}) {
    const link = document.createElement("a");
    link.href = url || URL.createObjectURL(new Blob([content], {type: 'text/plain'}));
    link.download = name;
    document.body.appendChild(link); // Required for this to work in FireFox
    link.click();
    document.body.removeChild(link);
}

export function selectFile(onSelect) {
    const input = document.createElement("input");
    input.type = "file";
    input.onchange = e => onSelect(e.target.files[0]);
    document.body.appendChild(input);       // Required for this to work in FireFox
    input.click();
    document.body.removeChild(input);
}

export function readFile(file, onRead) {
    const reader = new FileReader();
    reader.onload = event => onRead(event.target.result);
    reader.readAsText(file);
    //reader.readAsBinaryString(file)
}


export function copyToClipboard(text) {
    navigator.clipboard.writeText(text).then(() => 0, err => console.error('Async: Could not copy text:', err));
}

let cacheRemPixels;

export function remPixels() {
    if (cacheRemPixels) return cacheRemPixels;
    const calcRemPixels = () => (cacheRemPixels = parseFloat(getComputedStyle(document.documentElement).fontSize));
    window.addEventListener("resize", calcRemPixels);
    return calcRemPixels();
}

export function clsJoin(...args) {
    return args.filter(a => a).join(" ");
}

export function clsNms(...args) {
    const arr = args.filter(a => a);
    return arr.length? {className: arr.join(" ")} : {};
}

const nav = window.navigator;
export const locale = (nav.languages && nav.languages[0]) || nav.userLanguage || nav.language;

//const dateTimeLocaleOptions = {dateStyle: "short", timeStyle: "short"};
const dateTimeLocaleOptions = {month: '2-digit', day: "numeric", year: 'numeric', hour: "numeric", minute: "2-digit"};

function procDate(dt, flags = {}) {
    const {never, invalid} = flags;
    dt = dt?.cellData ?? dt;
    if (!dt) return "";
    if (typeof dt === "string" || typeof dt === "number") dt = new Date(dt);
    if (isNaN(dt)) return invalid || "(invalid)";
    if (dt < 0) return never || "(never)";
    return dt;
}

export function dispDateTime(dt) {
    dt = procDate(dt);
    if (typeof dt === "string") return dt;
    return dt.toLocaleString(undefined, dateTimeLocaleOptions).split(",").join("");
}

export function getAgoTip(date, opts) {
    const ago = getAgo(date, opts);
    return ago && <span title={dispDateTime(date)}>{ago}</span>;
}

export function dateToPassed(dt, flags = {}) {
    const {short, noDate, tooltip} = flags;
    dt = procDate(dt, flags);
    if (typeof dt === "string") return dt;
    const sec = (Date.now() - dt) / 1000 >> 0;
    const unit = units.find((u,i) => i === units.length-1 || sec < unitVals[i+1]*2);
    const val = sec / secUnits[unit] >> 0;
    const str = val + (short? unit[0] : " "+ unit + (val.plural));
    return noDate? str :
        tooltip?
            <span title={dispDateTime(dt)}>{str}</span>
            :
            `${str} ago (${dispDateTime(dt)})`;
}

export function emailToName(val) {
    const email = val?.cellData ?? val;
    return typeof email === "string" ? email.before('@').capitalize() : null;
}

export function msToExpDate(val) {
    const expDate = val?.cellData ?? val;
    return expDate? msToInputDate(expDate - MS_DAY) : null;
}

export function msToExpDays(val) {
    const expDate = val?.cellData ?? val;
    return msToDays(expDate);
}

export const LoadingAnim = ({className, classNameSize, noSrvCache}) => (
    <div data-test="loading" className={`flex flex-col justify-center items-center relative gap-2 ${className}`}>
        <div className={`animate-spin border-2 border-r-transparent rounded-full ${classNameSize || "w-3 h-3"}`}/>
        {noSrvCache && <div data-test="no-server-cache-msg">Server is calculating results for this data set for the first time.</div>}
        {noSrvCache && <div>This might take several minutes...</div>}
    </div>
);

export const MkSvg = ({className, d, pStrokes, style, viewBox, ...props}) => (
    <svg xmlns="http://www.w3.org/2000/svg" className={className || "h-4 w-4"}
         {...!props.fill && props.strokeWidth !== "0" && {fill: "none"}} viewBox={viewBox ?? "0 0 24 24"} stroke="currentColor" {...props}
    >
        {[].concat(d).map((dv, i) =>
            <path
                {...(pStrokes || {strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: props.strokeWidth ?? 2})}
                d={dv} style={Array.isArray(style)? style[i] : style} key={i}/>
        )}
    </svg>
);

const numClasses = {
    n: "text-green-200",
    k: "text-green-500",
    M: "text-blue-500",
    G: "text-red-500",
}
export function dispAbvNum2Clr(val, szOrgs) {
    const v = val?.cellData ?? val;
    const s = abvNum(v);
    const title = [abvNumEx(v, {all: 1}), ...szOrgs?.map(v => abvNumEx(v, {all: 1})) ?? []].join('\n');
    return <span {...clsNms("whitespace-nowrap", numClasses[s.slice(-1)] || numClasses.n)} title={title}>{s}</span>;
}


export const allowDrag = obj => ({
    draggable: true,
    onDragStart: e => e.dataTransfer.setData("obj", JSON.stringify(obj))
});

export const allowDrop = (obj, onDrop) => ({
    onDragOver: e => e.preventDefault(),
    onDrop: e => {
        e.preventDefault();                             // prevent the browser default handling of the data (default is open as link on drop)
        onDrop(JSON.parse(e.dataTransfer.getData("obj")), obj);
    }
});

export const allowDragDrop = (obj, onDrop) => ({...allowDrag(obj), ...allowDrop(obj, onDrop)});

// returns true if keypress handled
//
export function onKeyHandler(e, keyHandlers) {
    keyHandlers = funcOrValOr(keyHandlers);
    const fn = keyHandlers[e.code] ?? keyHandlers[e.key];
    if (!fn) return;
    let unhandle;
    fn(e, () => unhandle = true);
    if (unhandle) return;
    e.stopPropagation();
    e.preventDefault();
    return true;
}

export function genOnKeys(keyHandlers) {
    return {onKeyDown: e => onKeyHandler(e, keyHandlers)};
}

export function onTitle(title) {
    if (!(title ?? true)) return {};        // undefined or null?
    if (typeof title !== "function") return {title};
    return {onMouseEnter: e => e.target.title = title(e)};
}

export function tooltipIfNeeded({target}) {
    if (target.scrollWidth > target.clientWidth)
        target.title = target.innerText; // Set the tooltip to the full text content
}

export function titleOnHover() {
    return {
        onMouseEnter: ({target}) => {
            if (!target.title && target.scrollWidth > target.clientWidth && !target.childElementCount)
                target.title = target.innerText;
        }
    }
}

// Settings
export let classNameScrollbar = "", classNameScrollbarThin = "";

export const setClassNameScrollbar = (norm, thin) => {
    classNameScrollbar = norm;
    classNameScrollbarThin = thin;
}
